// useDeviceListSorting – sort config, requestSort, getSortIndicator, sortedRows (getSortMeta).

import { useMemo, useState } from 'react';
import type { DeviceMasterRow, SortConfig } from '../../../../types/DeviceListTypes';
import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import type { CommandDdbItem } from '../../../../types/Commands';
import { getAckPercent, parseStatusInfo } from '../../CommandListView/CommandsListHelpers';

import type { UseDeviceListSortingParams, UseDeviceListSortingResult } from '../types/DeviceListViewTypes';

export type { UseDeviceListSortingParams, UseDeviceListSortingResult };

export function useDeviceListSorting({
  rows,
  columns,
  idKey,
  statusMap,
  tlsMap,
  onlineMap,
  displayedLatestCommandMap,
  lifecycleMap = {},
  attributesMap = {},
}: UseDeviceListSortingParams): UseDeviceListSortingResult {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: '__online',
    direction: 'desc',
  });

  function requestSort(key: string) {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  }

  function getSortIndicator(key: string) {
    if (!sortConfig || sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  }

  type SortMeta = { value: string | number; empty: boolean };

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;
    if (!columns.length) return rows;

    const { key, direction } = sortConfig;

    const getSortMeta = (row: DeviceMasterRow): SortMeta => {
      const deviceId = idKey ? (row[idKey] as string | undefined) : undefined;
      const status = deviceId ? statusMap[deviceId] : undefined;
      const latestCmd = deviceId ? displayedLatestCommandMap[deviceId] : undefined;

      if (key === '__online') {
        const online = !!(deviceId && onlineMap[deviceId]);
        return { value: online ? 1 : 0, empty: !online };
      }

      if (key === '__fw') {
        const v = (status?.fw as string) || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__modem_fw') {
        const v = (status?.modem_fw as string) || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__gnss_fw') {
        const v = (status?.gnss_fw as string) || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__batt') {
        if (typeof status?.batt_mv === 'number') return { value: status.batt_mv, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__last') {
        if (typeof status?.updatedAt === 'number') return { value: status.updatedAt, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__tls') {
        if (!deviceId) return { value: 0, empty: true };
        const hasTls = Object.prototype.hasOwnProperty.call(tlsMap, deviceId) && tlsMap[deviceId];
        if (hasTls === true) return { value: 2, empty: false };
        if (hasTls === false) return { value: 1, empty: false };
        return { value: 0, empty: true };
      }

      if (
        key === '__lifecycle_lost'
        || key === '__lifecycle_decommissioned'
        || key === '__lifecycle_damaged'
        || key === '__lifecycle_errored'
      ) {
        if (!deviceId) return { value: 0, empty: true };
        const lm = lifecycleMap[deviceId];
        const on =
          key === '__lifecycle_lost'
            ? lm?.lost === true
            : key === '__lifecycle_decommissioned'
              ? lm?.decommissioned === true
              : key === '__lifecycle_damaged'
                ? lm?.damaged === true
                : lm?.errored === true;
        return { value: on ? 1 : 0, empty: false };
      }

      if (key === '__lifecycle_note') {
        if (!deviceId) return { value: '', empty: true };
        const v = String(lifecycleMap[deviceId]?.reason ?? '').trim();
        return { value: v.toLowerCase(), empty: v.length === 0 };
      }

      if (key === '__attr_race_count') {
        if (!deviceId) return { value: 0, empty: true };
        const v = attributesMap[deviceId]?.raceCount;
        if (typeof v === 'number' && Number.isFinite(v)) return { value: v, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__attr_waterproofed_by') {
        if (!deviceId) return { value: '', empty: true };
        const v = String(attributesMap[deviceId]?.waterproofedBy ?? '').trim();
        return { value: v.toLowerCase(), empty: v.length === 0 };
      }

      if (key === '__race_log') {
        if (typeof status?.race_log_size === 'number')
          return { value: status.race_log_size, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__debug_log') {
        if (typeof status?.debug_log_size === 'number')
          return { value: status.debug_log_size, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__batt_ma') {
        if (typeof status?.batt_ma === 'number') return { value: status.batt_ma, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__puck') {
        const isOnline = !!(deviceId && onlineMap[deviceId]);
        if (!isOnline) return { value: 0, empty: false };
        if (typeof status?.puck === 'number') return { value: status.puck, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__reason') {
        const v = (status?.last_state_reason as string) || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__gga') {
        const lat = status?.lat;
        const lng = status?.lng;
        if (typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng)) {
          const v = `${lat.toFixed(4)},${lng.toFixed(4)}`;
          return { value: v, empty: false };
        }
        return { value: '', empty: true };
      }

      if (key === '__location_updated') {
        if (typeof status?.last_location_updated_at === 'number') {
          const ts = status.last_location_updated_at;
          return { value: ts > 1e12 ? ts : ts * 1000, empty: false };
        }
        return { value: 0, empty: true };
      }

      if (key === '__last_cmd_id') {
        const v = latestCmd?.commandId || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__last_cmd_type') {
        const v = latestCmd?.type || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__last_cmd_target') {
        const v = latestCmd?.targetVersion || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__last_cmd_status') {
        const v = latestCmd?.status || '';
        return { value: v, empty: v.trim().length === 0 };
      }

      if (key === '__last_cmd_ack_percent') {
        const raw = typeof latestCmd?.status_info === 'string' ? latestCmd.status_info : '';
        const ack = raw ? parseStatusInfo(raw) : undefined;
        const pctRaw = getAckPercent(ack);
        const hasPct =
          typeof pctRaw === 'number' && Number.isFinite(pctRaw) && pctRaw >= 0;
        const pct = hasPct ? Math.max(0, Math.min(100, pctRaw)) : -1;
        if (pct >= 0) return { value: pct, empty: false };
        return { value: 0, empty: true };
      }

      if (key === '__last_cmd_updated') {
        if (typeof latestCmd?.updatedAt === 'number')
          return { value: latestCmd.updatedAt, empty: false };
        return { value: 0, empty: true };
      }

      const raw = (row as Record<string, unknown>)[key] as unknown;
      if (raw === null || raw === undefined || raw === '') return { value: '', empty: true };
      if (typeof raw === 'number') return { value: raw, empty: false };
      const v = String(raw);
      return { value: v, empty: v.trim().length === 0 };
    };

    // Compute each row's sort key once (O(n)) rather than re-deriving it on every
    // comparator call (O(n log n) getSortMeta invocations), which is costly for big lists.
    const decorated = rows.map((row, index) => ({ row, index, meta: getSortMeta(row) }));

    decorated.sort((a, b) => {
      const aMeta = a.meta;
      const bMeta = b.meta;

      if (aMeta.empty && !bMeta.empty) return 1;
      if (!aMeta.empty && bMeta.empty) return -1;
      if (aMeta.empty && bMeta.empty) return a.index - b.index;

      const aVal = aMeta.value;
      const bVal = bMeta.value;

      let cmp: number;
      if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
      else cmp = String(aVal).localeCompare(String(bVal));

      // Stable tie-break on original index so equal rows keep their incoming order.
      if (cmp === 0) return a.index - b.index;

      return direction === 'asc' ? cmp : -cmp;
    });

    return decorated.map((entry) => entry.row);
  }, [
    rows,
    sortConfig,
    columns,
    statusMap,
    tlsMap,
    idKey,
    onlineMap,
    displayedLatestCommandMap,
    lifecycleMap,
    attributesMap,
  ]);

  return { sortConfig, requestSort, getSortIndicator, sortedRows };
}
